import { DEFAULT_URL, FNO_ONBOARDING_API_URLS, DERIVATIVE_API_URLS, BANK_MANDATE_API_URLS, KYC_API_URLS, EKYC_API_URLS, COMMODITY_API_URLS, USER_DATA, MAIN_ONB_API_URL, DELTA_JOURNEY_API_URL, RESET_USER_ONBOARDING_JOURNEY_API_URL, ACTIVATE_JOURNEY } from '../../../core/services';
import { axiosInstance } from '../config';

import { JOURNEY_TYPES, DATA_TYPE, SUBMISSION_MODE } from '../../../core/constants';
import { ValueOf } from 'src/utils/types';
import { CONSTANTS } from '@adapter/web-storage';
import { cacheApiResponseInLocalStorage } from '../helpers';
import { getRefreshToken } from '../storage';

export function refreshToken() {
  const url = '/v1/api/user/v1/auth/token/refresh';

  const data = {
    refreshToken: `Bearer ${getRefreshToken()}`,
  };

  return axiosInstance.post(url, data);
}

export const checkFeatureAction = () => {
  const url = DEFAULT_URL.FEATURE_ACTION;

  const payload = {
    feature: 'investOnboarding',
  };
  return axiosInstance.post(url, payload);
}

export const activateJourney = () => {
  const url = ACTIVATE_JOURNEY;

  return axiosInstance.get(url);
}

export function sendLinkToMobile(mobileNumber: string) {

  const url = `/v1/api/user/v1/send/app_link_sms?mobile_number=${mobileNumber}`;

  return axiosInstance.post(url, undefined, { metadata: { urlTemplate: '/v1/api/user/v1/send/app_link_sms' } } as any);
}

const params = (journeyType: ValueOf<typeof JOURNEY_TYPES>) => {
  return journeyType === JOURNEY_TYPES.MAIN_ONBOARDING
    ? {}
    : {
      journeyType: journeyType as ValueOf<typeof JOURNEY_TYPES>,
    };
};

// TODO :: Revisit this once journey is clear.
const progressAPIParams = (journeyType: ValueOf<typeof JOURNEY_TYPES>) => {
  return journeyType === JOURNEY_TYPES.MAIN_ONBOARDING || journeyType === JOURNEY_TYPES.NRI_ONBOARDING_V2
    ? {}
    : {
      journeyType: journeyType as ValueOf<typeof JOURNEY_TYPES>,
    };
};

const apiUrl = (journeyType: ValueOf<typeof JOURNEY_TYPES>) => {
  return journeyType === JOURNEY_TYPES.MAIN_ONBOARDING
    ? MAIN_ONB_API_URL
    : DELTA_JOURNEY_API_URL;
};

// TODO :: Revisit this once journey is clear.
const progressApiUrl = (journeyType: ValueOf<typeof JOURNEY_TYPES>) => {
  return journeyType === JOURNEY_TYPES.MAIN_ONBOARDING || journeyType === JOURNEY_TYPES.NRI_ONBOARDING_V2
    ? MAIN_ONB_API_URL
    : DELTA_JOURNEY_API_URL;
};

export function getUserAttributeDetails() {
  const url = USER_DATA.USER_ATTRIBUTES;

  return axiosInstance.get(url);
}

export function getOnlyUserDetails() {
  const url = USER_DATA.ONLY_USER_DATA;

  return axiosInstance.get(url);
}

export function getNomineeSummary() {
  const url = '/v1/api/user/v1/nominee_management/summary';

  return axiosInstance.get(url);
}

export function resetUserOnboardingJourney(journeyType: ValueOf<typeof JOURNEY_TYPES>) {
  const url = RESET_USER_ONBOARDING_JOURNEY_API_URL + '?journeyType=' + journeyType;

  return axiosInstance.post(url);
}

export function initProgress(journeyType: ValueOf<typeof JOURNEY_TYPES>) {
  const url = progressApiUrl(journeyType).PROGRESS_INIT;

  return axiosInstance.post(url, undefined, {
    params: { ...progressAPIParams(journeyType) }
  });
}

export function getUserBanksListV2(hardRefresh = false) {
  const url = BANK_MANDATE_API_URLS.BANK_MANDATE_V2;

  const scheme = {
    expiresInMin: CONSTANTS.ONE_HOUR_IN_MINUTES,
    useEncryption: true,
    hardRefresh,
  };

  return cacheApiResponseInLocalStorage(url, scheme); // why cache ?
}

export function getFIPBankList() {
  const url = FNO_ONBOARDING_API_URLS.FNO_FIP;

  return axiosInstance.get(url);
}

export function initProgressPoll(journeyType: ValueOf<typeof JOURNEY_TYPES>) {
  const url = progressApiUrl(journeyType).PROGRESS_POLL;

  return axiosInstance.get(url, {
    params: { ...progressAPIParams(journeyType) }
  });
}

export function initSubmission(
  journeyType: ValueOf<typeof JOURNEY_TYPES>,
  dataType: ValueOf<typeof DATA_TYPE>,
  submissionMode: ValueOf<typeof SUBMISSION_MODE>,
  payload: any
) {
  const url = apiUrl(journeyType).SUBMISSION_INIT;

  return axiosInstance.post(url, payload, {
    params: { ...params(journeyType), dataType, submissionMode }
  });
}

export function initSubmissionPoll(
  journeyType: ValueOf<typeof JOURNEY_TYPES>,
  pollId: string
) {
  const url = apiUrl(journeyType).SUBMISSION_POLL;

  return axiosInstance.get(url, {
    params: {
      pollId,
    }
  });
}

export function submitSubmission(
  journeyType: ValueOf<typeof JOURNEY_TYPES>,
  dataType: ValueOf<typeof DATA_TYPE>,
  submissionMode: ValueOf<typeof SUBMISSION_MODE>,
  payload: any
) {
  const url = apiUrl(journeyType).SUBMISSION_SUBMIT;

  return axiosInstance.post(url, payload, {
    params: {
      ...params(journeyType),
      submissionMode,
      dataType,
    }
  });
}

export const fetchBankList = () => {
  const url = BANK_MANDATE_API_URLS.FETCH_BANK_LIST;
  return axiosInstance.get(url);
};

export const fetchBranch = ({
  bankCode,
  bankName,
  branch = '',
}: {
  bankName: string;
  bankCode: string;
  branch?: string;
}) => {
  const url = BANK_MANDATE_API_URLS.FETCH_BANK_BRANCH;
  const params = {
    bank_code: bankCode,
    bank_name: bankName,
    branch,
    sort_by_bank: 'false',
    search_term: '*',
  };
  return axiosInstance.get(url, { params });
};

export function verifyIpOfUser() {
  const url = KYC_API_URLS.GEO_LOCATION_VERIFY_IP_LOCATION;

  return axiosInstance.get(url);
}

export const getHypervergeJwtToken = () => {
  const url = DEFAULT_URL.HYPERVERGE_JWT_TOKEN;

  return axiosInstance.get(url);
};

export const getEkycScreenConfigs = () => {
  const url = EKYC_API_URLS.EKYC_SCREEN_CONFIGS;;
  return axiosInstance.get(url);
};

type EkycPayload = {
  requestId: string;
  consentProvided: string;
  disclosureSeen: boolean;
  idNo: string;
  idType: string;
  userName: string;
};

export const EkycInitApi = (payload: EkycPayload) => {
  const url = EKYC_API_URLS.EKYC_INIT;
  return axiosInstance.post(url, payload);
};

type EkycValidateOtpPayload = {
  requestId: string;
  idNo: string;
  idType: string;
  otp: string;
};

export const EkycValidateOtpApi = (payload: EkycValidateOtpPayload) => {
  const url = EKYC_API_URLS.EKYC_VALIDATE_OTP;
  return axiosInstance.post(url, payload);
};

export function fetchPincodeDetails(pincode: string) {
  const url = DEFAULT_URL.PINCODE_DETAILS + '/' + pincode;

  return axiosInstance.get(url);
}

export function activateFnoOnboarding(isUserOptedForEquity: string = 'true', isUserOptedForCommodity: string = 'false') {
  const url = DERIVATIVE_API_URLS.INITIATE;


  if ((isUserOptedForEquity === 'false' && isUserOptedForCommodity === 'false') || !isUserOptedForEquity || !isUserOptedForCommodity) {
    const payload = {
      equity: 'true',
      commodity: 'false'
    };

    return axiosInstance.post(url, payload);

  } else {
    const payload = {
      equity: isUserOptedForEquity,
      commodity: isUserOptedForCommodity
    };

    return axiosInstance.post(url, payload);
  }
}
export const createConsentInit = (dataRefId: string, fipId: string) => {
  const url = FNO_ONBOARDING_API_URLS.AA_CONSENT_INIT;

  const payload = {
    dataRefId: dataRefId,
    fipIds: [fipId],
  };

  return axiosInstance.post(url, payload);
};

export const createConsentInitV2 = (dataRefId: string, fipId: string) => {
  const url = DERIVATIVE_API_URLS.AA_CONSENT_INIT;

  const payload = {
    dataRefId: dataRefId,
    fipIds: [fipId],
  };

  return axiosInstance.post(url, payload);
};


export const bankStatementDownloadInit = (
  dataRefId: string,
  callBackId: string
) => {
  const url = DERIVATIVE_API_URLS.AA_DOWNLOAD_INIT;

  const payload = {
    dataRefId: dataRefId,
    callBackId: callBackId,
  };

  return axiosInstance.post(url, payload);
};

export const bankStatementDownloadPoll = (
  dataRefId: string,
  callBackId: string
) => {
  const url = FNO_ONBOARDING_API_URLS.AA_DOWNLOAD_POLL;

  const params = {
    dataRefId: dataRefId,
    callBackId: callBackId,
  };

  return axiosInstance.get(url, { params });
};

export function registerBankUsingAccountNumber(data: any) {
  const url = BANK_MANDATE_API_URLS.REGISTER_BANK_PENNY_DROP_ADD;

  return axiosInstance.post(url, data);
}
export function checkBankVerificationStatusV2(bankVerificationId: string) {
  const newUrl = BANK_MANDATE_API_URLS.BANK_VERIFICATION_STATUS_PENNY_DROP;
  const params = {
    bank_verification_id: bankVerificationId,
  };

  return axiosInstance.get(newUrl, {
    params,
  });
}

export function updateFnoDerivate(activationId: string, valueChainParticipant: boolean) {
  const url = DERIVATIVE_API_URLS.UPDATE_REQUEST;

  return axiosInstance.post(url, { activationId, valueChainParticipant });
}

export function activateCommodities(
  profession: string,
  valueChainParticipant: boolean
) {
  const url = COMMODITY_API_URLS.ACTIVATE;

  return axiosInstance.post(url, { profession, valueChainParticipant });
}

export function updateRAAAuthorisation(quarterlySettlementType: string) {
  const url = BANK_MANDATE_API_URLS.UPDATE_RAA;

  return axiosInstance.post(url, undefined, {
    params: {
      quarterly_settlement_type: quarterlySettlementType,
    },
  });
}
